import React, { useEffect, useRef, forwardRef } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup, CircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '../styles/RouteMap.css';

// Fix per a les icones de Leaflet en React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Logo Strava petit per la llegenda
const StravaLogoSmall = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" style={{ marginRight: '4px' }}>
    <path fill="#FC4C02" d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0L0 17.944h6.121"/>
  </svg>
);

const createCustomIcon = (type) => {
  const colors = {
    drinking_water: '#1e90ff',
    shelter: '#ff6347',
    viewpoint: '#32cd32',
    peak: '#ff4500',
    picnic_site: '#8b4513',
    restaurant: '#ffa500',
    cafe: '#d2691e',
    parking: '#808080',
    toilets: '#9370db',
    spring: '#00bfff',
    bicycle: '#ff1493',
    default: '#667eea',
  };

  const color = colors[type] || colors.default;

  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background-color: ${color};
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      "></div>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
};

// Component intern per gestionar el marcador hover
const HoverMarker = ({ mapRef }) => {
  const map = useMap();
  const markerRef = useRef(null);

  useEffect(() => {
    if (mapRef) {
      mapRef.current = {
        updateHoverPosition: (km, coordinates) => {
          if (km === null) {
            if (markerRef.current) {
              map.removeLayer(markerRef.current);
              markerRef.current = null;
            }
            return;
          }

          let totalDistance = 0;
          let targetCoord = coordinates[0];

          for (let i = 1; i < coordinates.length; i++) {
            const prev = coordinates[i - 1];
            const curr = coordinates[i];
            
            const R = 6371;
            const dLat = (curr.lat - prev.lat) * Math.PI / 180;
            const dLon = (curr.lon - prev.lon) * Math.PI / 180;
            const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                      Math.cos(prev.lat * Math.PI / 180) * Math.cos(curr.lat * Math.PI / 180) *
                      Math.sin(dLon/2) * Math.sin(dLon/2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
            totalDistance += R * c;

            if (totalDistance >= km) {
              targetCoord = curr;
              break;
            }
          }

          if (!markerRef.current) {
            markerRef.current = L.circleMarker([targetCoord.lat, targetCoord.lon], {
              radius: 10,
              fillColor: '#f59e0b',
              color: 'white',
              weight: 3,
              opacity: 1,
              fillOpacity: 0.9
            }).addTo(map);
          } else {
            markerRef.current.setLatLng([targetCoord.lat, targetCoord.lon]);
          }
        }
      };
    }
  }, [map, mapRef]);

  return null;
};

// Helper per formatar temps
const formatTime = (seconds) => {
  if (!seconds) return null;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const RouteMap = forwardRef(({ coordinates, pois, stravaSegments }, ref) => {
  const mapRef = useRef();
  const routeCoords = coordinates.map((coord) => [coord.lat, coord.lon]);
  
  const center = routeCoords.length > 0 ? routeCoords[Math.floor(routeCoords.length / 2)] : [41.3851, 2.1734];

  useEffect(() => {
    if (mapRef.current && routeCoords.length > 0) {
      const bounds = L.latLngBounds(routeCoords);
      mapRef.current.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [routeCoords]);

  return (
    <div className="route-map-container fade-in">
      <MapContainer center={center} zoom={13} ref={mapRef} style={{ height: '100%', width: '100%' }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <HoverMarker mapRef={ref} />

        {/* Ruta principal */}
        <Polyline positions={routeCoords} color="#667eea" weight={5} opacity={0.7} />

        {/* Marcadors inici/final */}
        {routeCoords.length > 0 && (
          <>
            <Marker position={routeCoords[0]}>
              <Popup><strong>🚀 Inici de la ruta</strong></Popup>
            </Marker>
            <Marker position={routeCoords[routeCoords.length - 1]}>
              <Popup><strong>🏁 Final de la ruta</strong></Popup>
            </Marker>
          </>
        )}

        {/* SEGMENTS STRAVA - AMB POLYLINE REAL */}
        {stravaSegments && stravaSegments.length > 0 && (
          <>
            {stravaSegments.slice(0, 15).map((segment, index) => {
              if (!segment.start_lat || !segment.start_lon) return null;
              
              const startPos = [segment.start_lat, segment.start_lon];
              
              const segmentPath = segment.polyline && segment.polyline.length > 1
                ? segment.polyline
                : (segment.end_lat && segment.end_lon ? [startPos, [segment.end_lat, segment.end_lon]] : null);
              
              return (
                <React.Fragment key={`strava-group-${segment.id || index}`}>
                  {/* Línia del segment */}
                  {segmentPath && (
                    <Polyline
                      positions={segmentPath}
                      pathOptions={{
                        color: '#fc4c02',
                        weight: 6,
                        opacity: 0.85,
                        lineCap: 'round',
                        lineJoin: 'round'
                      }}
                    />
                  )}

                  {/* Punt d'inici clicable */}
                  <CircleMarker
                    center={startPos}
                    radius={7}
                    pathOptions={{
                      color: 'white',
                      fillColor: '#fc4c02',
                      fillOpacity: 1,
                      weight: 2
                    }}
                  >
                    <Popup>
                      <div className="strava-popup" style={{ minWidth: '200px' }}>
                        {/* Header amb logo Strava */}
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '6px',
                          marginBottom: '8px',
                          paddingBottom: '6px',
                          borderBottom: '1px solid #fee2e2'
                        }}>
                          <svg width="16" height="16" viewBox="0 0 24 24">
                            <path fill="#FC4C02" d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0L0 17.944h6.121"/>
                          </svg>
                          <strong style={{ color: '#fc4c02', fontSize: '0.95rem' }}>
                            {segment.name || 'Segment de Strava'}
                          </strong>
                        </div>
                        
                        {/* Dades del segment */}
                        <div style={{ fontSize: '0.85rem', color: '#374151' }}>
                          <p style={{ margin: '4px 0' }}>📏 Distància: <strong>{(segment.distance || 0).toFixed(0)}m</strong></p>
                          <p style={{ margin: '4px 0' }}>📈 Pendent: <strong>{(segment.avg_grade || 0).toFixed(1)}%</strong></p>
                          {segment.kom_time_sec && (
                            <p style={{ margin: '4px 0' }}>
                              🏆 KOM: <strong>{formatTime(segment.kom_time_sec)}</strong>
                            </p>
                          )}
                          {segment.qom_time_sec && (
                            <p style={{ margin: '4px 0' }}>
                              👑 QOM: <strong>{formatTime(segment.qom_time_sec)}</strong>
                            </p>
                          )}
                          {segment.start_km !== undefined && (
                            <p style={{ margin: '4px 0', color: '#6b7280' }}>📍 KM {segment.start_km}</p>
                          )}
                        </div>
                        
                        {/* Link "View on Strava" - OBLIGATORI segons Brand Guidelines */}
                        {segment.id && (
                          <a 
                            href={`https://www.strava.com/segments/${segment.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              marginTop: '10px',
                              padding: '5px 10px',
                              backgroundColor: '#FC4C02',
                              color: 'white',
                              textDecoration: 'none',
                              borderRadius: '4px',
                              fontSize: '0.8rem',
                              fontWeight: '600'
                            }}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                              <polyline points="15 3 21 3 21 9"/>
                              <line x1="10" y1="14" x2="21" y2="3"/>
                            </svg>
                            View on Strava
                          </a>
                        )}
                      </div>
                    </Popup>
                  </CircleMarker>
                </React.Fragment>
              );
            })}
          </>
        )}

        {/* POIs (Punts d'interès) */}
        {pois && pois.map((poi, index) => (
          <Marker
            key={`poi-${index}`}
            position={[poi.lat, poi.lon]}
            icon={createCustomIcon(poi.type)}
          >
            <Popup>
              <div className="poi-popup">
                <strong>{poi.name}</strong>
                <p>Tipus: {poi.type}</p>
                {poi.distance_from_start && <p>KM: {poi.distance_from_start.toFixed(1)}</p>}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {/* Llegenda del mapa - AMB ATRIBUCIÓ STRAVA */}
      <div className="map-legend">
        <div className="legend-item">
          <div className="legend-line route-line"></div>
          <span>Ruta principal</span>
        </div>
        {stravaSegments && stravaSegments.length > 0 && (
          <div className="legend-item">
            <div className="legend-line" style={{ backgroundColor: '#fc4c02', height: '3px' }}></div>
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <StravaLogoSmall />
              Segments ({stravaSegments.length})
            </span>
          </div>
        )}
      </div>
    </div>
  );
});

export default RouteMap;